import { applyApiRequestErrorGrouping } from "@cede/utils";

type SentryException = {
  value?: string;
};

type SentryEventLike = {
  exception?: {
    values?: SentryException[];
  };
  message?: string;
  fingerprint?: string[];
  tags?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
};

type SentryEventHintLike = {
  originalException?: unknown;
};

// EIP-1193 provider error codes that represent expected wallet UX rather than app bugs:
// 4001 — user rejected the request; 4900 — provider disconnected from all chains;
// 4901 — provider not connected to the requested chain.
const EXPECTED_WALLET_ERROR_CODES = new Set([4001, 4900, 4901]);

// Wallet rejections often arrive as non-Error promise rejections (a bare string or a
// `{ code, message }` object), so the meaningful text lands in the rejection itself rather than
// the top-level event message that `ignoreErrors` matches against. Match the underlying text too.
const EXPECTED_WALLET_MESSAGE_PATTERNS = [
  /user (?:has )?rejected (?:the )?request/i,
  /user (?:has )?denied (?:transaction|message|signature)/i,
  /provider is disconnected from .*chain/i,
];

function isExpectedWalletProviderEvent(event: SentryEventLike, hint?: SentryEventHintLike): boolean {
  const original = hint?.originalException;
  const code = original && typeof original === "object" ? (original as { code?: unknown }).code : undefined;
  if (typeof code === "number" && EXPECTED_WALLET_ERROR_CODES.has(code)) {
    return true;
  }

  const originalMessage = typeof original === "string" ? original : (original as { message?: unknown })?.message;
  const texts: unknown[] = [originalMessage, event.exception?.values?.[0]?.value, event.message];
  return texts.some((text) => typeof text === "string" && EXPECTED_WALLET_MESSAGE_PATTERNS.some((p) => p.test(text)));
}

// Client `beforeSend`: drop expected wallet-provider noise (user-cancelled signatures/transactions
// and provider disconnects). These arrive as object/string promise rejections that `ignoreErrors`
// cannot match, so they are filtered here instead.
export function dropExpectedWalletProviderEvent<TEvent extends SentryEventLike>(
  event: TEvent,
  hint?: SentryEventHintLike,
): TEvent | null {
  return isExpectedWalletProviderEvent(event, hint) ? null : event;
}

/**
 * The single `beforeSend` for every Hub runtime (browser, server, edge): drop
 * expected wallet noise, then give API transport failures a per-endpoint
 * fingerprint so they stop sharing one issue.
 */
export function processHubSentryEvent<TEvent extends SentryEventLike>(
  event: TEvent,
  hint?: SentryEventHintLike,
): TEvent | null {
  const kept = dropExpectedWalletProviderEvent(event, hint);
  return kept && applyApiRequestErrorGrouping(kept, hint);
}
